import { useCallback, useEffect, useRef, useState } from "react";
import { api, type APIResult } from "../../api/client";
import { useAppState } from "../../hooks/useAppState";
import { classNames, formatDateTime } from "../../lib/format";
import { isTerminal, statusBadgeClass } from "../../lib/status";
import { PageHeader } from "../PageHeader";
import { ResponseCard } from "../ResponseCard";
import { SectionCard } from "../SectionCard";
import type { StatusResponse, JobStatus } from "../../types/routecore";

// Tab de acompanhamento do job.
//
// Quando "Polling ligado" e o status não é terminal, faz GET status a
// cada 2s. Mostra:
//
//   - status atual com badge colorido
//   - progress bar (progress_percent)
//   - timestamps (created/started/completed)
//   - mensagem de erro (failed/timeout)
//   - botões para Cancelar e Retry (delegam para as outras tabs)
type Props = {
  onNavigate: (tabId: string) => void;
};

const POLL_INTERVAL_MS = 2000;

export function TabJobStatus({ onNavigate }: Props) {
  const { trackedJobId, setTrackedJobId, lastJobIds } = useAppState();
  const [input, setInput] = useState(trackedJobId ?? "");
  const [result, setResult] = useState<APIResult<StatusResponse> | null>(null);
  const [polling, setPolling] = useState(true);
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);

  const status = result?.data?.status as JobStatus | undefined;
  const terminal = isTerminal(status);

  const fetchOnce = useCallback(async () => {
    if (!input || inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    const r = await api.getJob(input.trim());
    setResult(r as APIResult<StatusResponse>);
    setLoading(false);
    inFlight.current = false;
  }, [input]);

  useEffect(() => {
    if (!input || !polling || terminal) return;
    const id = setInterval(() => {
      void fetchOnce();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [input, polling, terminal, fetchOnce]);

  useEffect(() => {
    setInput(trackedJobId ?? "");
  }, [trackedJobId]);

  function applyId(id: string) {
    setTrackedJobId(id);
    setInput(id);
    setResult(null);
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Status do job"
        subtitle="Polling automático até o job atingir estado terminal."
        endpoint={{ method: "GET", path: "/v1/route-optimization/jobs/:id" }}
      />

      <SectionCard
        title="Job rastreado"
        description="Cole um job_id ou escolha um dos últimos criados nesta sessão."
        actions={
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-xs text-ink-700">
              <input
                type="checkbox"
                className="size-3.5 accent-brand-600"
                checked={polling}
                onChange={(e) => setPolling(e.target.checked)}
              />
              auto-poll (2s)
            </label>
            <button
              type="button"
              className="ks-btn-primary text-xs"
              disabled={loading || !input}
              onClick={fetchOnce}
            >
              {loading ? "consultando..." : "Consultar agora"}
            </button>
          </div>
        }
      >
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            className="ks-input ks-code flex-1"
            placeholder="00000000-0000-0000-0000-000000000000"
            value={input}
            onChange={(e) => setInput(e.target.value.trim())}
          />
          <button
            type="button"
            className="ks-btn-secondary text-xs"
            disabled={!result?.data?.result_url}
            onClick={() => onNavigate("job-result")}
          >
            ver resultado →
          </button>
          <button
            type="button"
            className="ks-btn-secondary text-xs"
            disabled={!input || terminal}
            onClick={() => onNavigate("job-actions")}
          >
            cancelar / retry →
          </button>
        </div>
        {lastJobIds.length > 0 && (
          <div className="mt-3">
            <p className="ks-label">Jobs criados nesta sessão</p>
            <div className="mt-1 flex flex-wrap gap-1">
              {lastJobIds.map((id) => (
                <button
                  key={id}
                  type="button"
                  className={classNames(
                    "ks-code rounded-md border px-2 py-1 text-[11px] transition",
                    id === input
                      ? "border-brand-500 bg-brand-50 text-brand-800"
                      : "border-ink-200 bg-white text-ink-700 hover:bg-ink-100",
                  )}
                  onClick={() => applyId(id)}
                >
                  {id.slice(0, 8)}…
                </button>
              ))}
            </div>
          </div>
        )}
      </SectionCard>

      {result?.data && (
        <SectionCard title="Resumo">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`ks-tag ${statusBadgeClass(status)}`}>{status ?? "—"}</span>
            <span className="text-xs text-ink-500">
              progresso: {result.data.progress_percent?.toFixed(1) ?? "—"}%
            </span>
            {terminal && <span className="ks-tag bg-ink-200 text-ink-700">terminal</span>}
            {!terminal && polling && (
              <span className="ks-tag bg-sky-100 text-sky-800">
                <span className="size-1.5 animate-pulse rounded-full bg-sky-600" />
                polling ativo
              </span>
            )}
          </div>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-ink-100">
            <div
              className="h-full bg-brand-500 transition-all"
              style={{ width: `${Math.min(100, Math.max(0, result.data.progress_percent ?? 0))}%` }}
            />
          </div>
          <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-3">
            <Row label="Criado em" value={formatDateTime(result.data.created_at)} />
            <Row label="Iniciado em" value={formatDateTime(result.data.started_at)} />
            <Row label="Finalizado em" value={formatDateTime(result.data.completed_at)} />
            <Row
              label="Tempo de processamento"
              value={
                result.data.processing_time_seconds
                  ? `${result.data.processing_time_seconds.toFixed(2)} s`
                  : "—"
              }
            />
            {result.data.error_message && (
              <div className="sm:col-span-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2">
                <p className="ks-label text-rose-700">Mensagem de erro</p>
                <p className="text-rose-900">{result.data.error_message}</p>
              </div>
            )}
          </dl>
        </SectionCard>
      )}

      <ResponseCard
        result={result}
        loading={loading}
        emptyHint="Cole um job_id e clique em Consultar agora."
      />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-ink-200 px-3 py-2">
      <dt className="ks-label">{label}</dt>
      <dd className="text-sm text-ink-800">{value}</dd>
    </div>
  );
}
